import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useLocation, useParams } from 'react-router-dom'
import _ from 'lodash'
import moment from 'moment'

import { getOrderDetail } from '@actions/order'
import { closeLoader, openLoader } from '@actions/loader'
import { closeModal, openModal } from '@actions/modal'
import { showToastNotification } from '../../../actions/toastNotification'
import {
  approveOrder,
  blobDownload,
  cancelOrder,
  cancelOrderItem,
  getSubOrderNf,
  listVariables,
} from '@services/sm-api'
import ConfirmDialog from '@shared/ConfirmDialog'
import PermissionsChecker from '@shared/PermissionsChecker'
import FileDownloader from '../../../shared/FileDownloader'
import Receipt from '@components/Receipt'
import { PixPaymentModal } from '@components/Pix/PixPaymentModal'
import { formatMoney } from '@shared/FormatMoney'
import { orderStatus as OS } from '@utils/orderStatus'
import { paymentStatus } from '@utils/paymentStatus'
import { buildTrackingSteps } from '@shared/trackingSteps'

import boleto from '../../OrderDetail/images/001-bar-code.png'
import boleto_hibrido from '../../OrderDetail/images/boleto-hibrido.png'
import pixIcon from '../../OrderDetail/images/pix.png'
import credit from '../../OrderDetail/images/credito.png'
import twoCards from '../../OrderDetail/images/two-cards.png'

import V2AccountLayout from '../V2AccountLayout'
import V2StatusPill from '../V2StatusPill'

// Detalhe do pedido no V2 — porte fiel do design "Gestão de Pedidos".
// TODAS as regras do OrderDetail V1 preservadas (MATRIZ MC-20..MC-52): forma de
// pagamento R/B/H/P + dois cartões, 2ª via NP/BAP, chave PIX em PW, cancelar/
// aprovar/exportar por permissão (comprador não vê), comprovante AP/PG/PAV,
// NF campanha>11 com janela no gesto, entrega por linha/B2C, tracker compartilhado
// (shared/trackingSteps), licenças digitais, item cancelado, aviso de volume.
// O "Andamento do pedido" agregado é adição visual do design (mesmos dados).

const microLabel = { display: 'block', fontSize: '11px', fontWeight: '700', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#8E8783' }
const microValue = { display: 'block', marginTop: '6px', fontSize: '15px', fontWeight: '600', color: '#373535' }
const ghostBtn = { height: '36px', padding: '0 14px', border: '1px solid #E3DBD7', borderRadius: '8px', background: '#fff', fontSize: '12.5px', fontWeight: '700', color: '#6B6664', cursor: 'pointer' }
const dangerBtn = { height: '36px', padding: '0 14px', border: '1px solid #F3C9CB', borderRadius: '8px', background: '#fff', fontSize: '12.5px', fontWeight: '700', color: '#D8262C', cursor: 'pointer' }

const PAYMENT_BASE = {
  R: { icon: credit, name: 'Cartão de crédito', machineName: 'credit_card' },
  B: { icon: boleto, name: 'Boleto', machineName: 'billet' },
  H: { icon: boleto_hibrido, name: 'Boleto/PIX', machineName: 'billet' },
  P: { icon: pixIcon, name: 'PIX', machineName: 'pix' },
  undefined: { icon: credit, name: 'Cartão de crédito' },
}

const isServiceLine = sub_order => !!sub_order.business_line.match(/SER/gm)

const formatOldTrackingLink = tracking_link => {
  if (!tracking_link) return null
  let loggiLink = tracking_link.replace('https://www.loggi.com/rastreador/', '')
  let params = loggiLink.split('/')
  if (params.length === 2) {
    return 'https://www.loggi.com/rastreador/' + params[1]
  }
  return tracking_link
}

export default function V2OrderDetail() {
  const dispatch = useDispatch()
  const { order_id } = useParams()
  // quem chega pelo relatório administrativo volta para ele, não para "Meus pedidos"
  const location = useLocation()
  const from = location.state && location.state.from
  const back = from && String(from).startsWith('/v2/admin') ? { to: from, label: '← Voltar ao relatório' } : { to: '/v2/conta/pedidos', label: '← Meus pedidos' }
  const order = useSelector(state => state.order.active)

  const [orderStatus, setOrderStatus] = useState('')
  const [subOrdersStatus, setSubOrdersStatus] = useState({})
  const [variables, setVariables] = useState({ licenseActivationLink: null, hybridBilletActivation: null })
  const [showPixModal, setShowPixModal] = useState(false)
  const [openSubs, setOpenSubs] = useState({})
  const [collapsedStudents, setCollapsedStudents] = useState({})
  const [nfAlert, setNfAlert] = useState({})
  const [nfLoading, setNfLoading] = useState({})

  useEffect(() => {
    dispatch(getOrderDetail({ group: 'voucher', order_id }))
    listVariables().then(vars => {
      setVariables({
        licenseActivationLink: (_.find(vars, { key: 'LICENSE_ACTIVATION_LINK' }) || {}).value || null,
        hybridBilletActivation: (_.find(vars, { key: 'HYBRID_BILLET_ACTIVATION' }) || {}).value || null,
      })
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order_id])

  // MC-22: status geral; MC-40: mapa de status por subpedido
  useEffect(() => {
    if (!order) return
    const validStatus =
      typeof order.order_status === 'string' ? order.order_status : typeof order.status === 'string' ? order.status : 'null'
    setOrderStatus(validStatus)

    const subs = order.sub_orders || {}
    const map = {}
    for (let student of Object.keys(subs)) {
      for (let sub of subs[student]) map[sub.id] = sub.status
    }
    setSubOrdersStatus(map)
  }, [order])

  // MC-26: tipo de pagamento efetivo (híbrido/PIX do boleto)
  const paymentType = useMemo(() => {
    if (!order) return undefined
    let payment_type = order.payment_type
    if (order.payment_type === 'B') {
      if (order.status === 'PG' || order.status === 'AP' || order.status === 'PAV') {
        if (order.txid) payment_type = 'P'
      } else if (variables.hybridBilletActivation == 1) {
        payment_type = 'H'
      }
    }
    return payment_type
  }, [order, variables.hybridBilletActivation])

  // MC-27: dois cartões / cartão único
  const payment = useMemo(() => {
    if (!order) return PAYMENT_BASE
    if (order.is_two_cards !== 1) {
      const cards = [{ card_number: order.last_card_numbers, total: order.original_total, payment_condition: order.payment_condition }]
      return { ...PAYMENT_BASE, R: { ...PAYMENT_BASE.R, cards } }
    }
    const subs = order.sub_orders || {}
    const groups = Object.keys(subs).map(key => subs[key])
    if (groups.length < 2) return PAYMENT_BASE
    const cards = [
      { ...groups[0][0], total: _.sumBy(groups[0].map(x => ({ ...x, amount: parseFloat(x.original_amount) })), 'amount') },
      { ...groups[1][0], total: _.sumBy(groups[1].map(x => ({ ...x, amount: parseFloat(x.original_amount) })), 'amount') },
    ]
    const freight = parseFloat(String(order.freight || '0').replace('R$', '').replace(',', '.'))
    cards[0].total = cards[0].total + freight
    return { ...PAYMENT_BASE, R: { icon: twoCards, name: 'Dois cartões', machineName: 'credit_card', cards } }
  }, [order])

  if (!order || String(order.order_id) !== String(order_id)) {
    return (
      <V2AccountLayout>
        <Link to={back.to} style={{ fontSize: '13px', fontWeight: '700', color: '#6B6664', textDecoration: 'none' }}>
          {back.label}
        </Link>
      </V2AccountLayout>
    )
  }

  const sub_orders = order.sub_orders || {}
  const allSubs = Object.values(sub_orders).flat()

  const _getSubOrderStatus = (subOrderId, isB2C) => {
    // MC-40: normalização B2B (pós-pagamento exibe "Pagamento confirmado")
    let code = subOrdersStatus[subOrderId]
    if (isB2C == 0 && (code == 'PP' || code == 'PF' || code == 'PT' || code == 'PET' || code == 'PE')) {
      code = 'AP'
    }
    return { sub_order_id: subOrderId, code, ...paymentStatus[code] }
  }

  const _getOrderMethod = subOrderId => {
    let method = ''
    const type = payment[order.payment_type] && payment[order.payment_type].machineName
    if (
      orderStatus === 'BAP' ||
      orderStatus === 'NP' ||
      (orderStatus === 'AP' && order.payment_type === 'R') ||
      (orderStatus === 'AP' && order.payment_type === 'B') ||
      (orderStatus === 'PE' && order.payment_type === 'R') ||
      (orderStatus === 'PE' && order.payment_type === 'B')
    ) {
      method = 'internal'
    } else {
      method = 'external'
    }
    const data = { order_id: order.order_id, type, method }
    if (subOrderId) {
      data.sub_order_id = subOrderId
      data.method = 'internal'
    }
    return data
  }

  const _setAllSubStatus = type => {
    setSubOrdersStatus(prev => {
      const next = { ...prev }
      for (let key of Object.keys(next)) next[key] = type
      return next
    })
  }

  const _handleCancelOrder = subOrderId => {
    const data = _getOrderMethod(subOrderId)
    dispatch(
      openModal(
        <ConfirmDialog
          text="Tem certeza que deseja cancelar esse pedido?"
          ok={() => {
            dispatch(closeModal())
            dispatch(openLoader())
            cancelOrder(data)
              .then(response => {
                if (response.status === 200) {
                  if (subOrderId) {
                    setSubOrdersStatus(prev => ({ ...prev, [subOrderId]: response.type }))
                  } else {
                    setOrderStatus(response.type)
                    _setAllSubStatus(response.type)
                  }
                  if (response.billet_paid) {
                    dispatch(
                      openModal(
                        <ConfirmDialog text="Boleto já foi pago e não será possível cancela-lo." okLabel="OK" ok={() => dispatch(closeModal())} />
                      )
                    )
                  }
                }
              })
              .finally(() => dispatch(closeLoader()))
          }}
          cancel={() => dispatch(closeModal())}
        />
      )
    )
  }

  const _handleApproveOrder = () => {
    const data = _getOrderMethod()
    dispatch(openLoader())
    approveOrder(data)
      .then(response => {
        if (response.status === 200) {
          setOrderStatus(response.type)
          _setAllSubStatus(response.type)
        }
      })
      .finally(() => dispatch(closeLoader()))
  }

  const _handleCancelOrderItem = item => {
    dispatch(
      openModal(
        <ConfirmDialog
          text={`Deseja realmente cancelar o item "${item.product_name}" do pedido SMD-${order.order_id}? Não é possível desfazer essa ação.`}
          ok={() => {
            dispatch(closeModal())
            dispatch(openLoader())
            cancelOrderItem(item.id)
              .then(() => dispatch(getOrderDetail({ group: 'voucher', order_id })))
              .then(() =>
                dispatch(
                  showToastNotification({
                    title: 'Cancelado com sucesso!',
                    text: 'O item foi cancelado e o valor final foi atualizado.',
                    variant: 'success',
                  })
                )
              )
              .finally(() => dispatch(closeLoader()))
          }}
          cancel={() => dispatch(closeModal())}
        />
      )
    )
  }

  const _handleExportOrder = () => {
    dispatch(openLoader())
    return blobDownload(`/orders/${order.order_id}/export`, {}).finally(() => dispatch(closeLoader()))
  }

  const _printReceipt = sub_order => {
    dispatch(
      openModal(
        <Receipt
          sub_order={sub_order}
          buyer_name={order.buyer_name}
          closeModal={() => dispatch(closeModal())}
          status={_getSubOrderStatus(sub_order.id, sub_order.school_is_b2c)}
          title="Comprovante de retirada"
        />
      )
    )
  }

  const renderInvoice = (invoiceWindow, danfe) => {
    invoiceWindow.document.open()
    invoiceWindow.document.write(
      `<style>body { margin: 0 } embed { margin: auto; width: 100dvw; height: 100dvh; } </style>
       <embed src='data:application/pdf;base64,${encodeURI(danfe)}'>`
    )
    invoiceWindow.document.close()
  }

  // MC-43: janela aberta no gesto do usuário (anti popup-block), consulta SAP ao vivo
  const _handleDownloadNf = async sub_order => {
    if (nfLoading[sub_order.id]) return
    setNfAlert(prev => ({ ...prev, [sub_order.id]: false }))
    setNfLoading(prev => ({ ...prev, [sub_order.id]: true }))
    const invoiceWindow = window.open('', '_blank', `width=${window.screen.availWidth * 0.5},height=${window.screen.availHeight * 0.7}`)
    if (invoiceWindow) invoiceWindow.document.write('<p style="font-family: sans-serif; padding: 16px">Consultando nota fiscal…</p>')
    try {
      const data = await getSubOrderNf(sub_order.id)
      if (data && data.danfe && invoiceWindow) {
        renderInvoice(invoiceWindow, data.danfe)
      } else {
        if (invoiceWindow) invoiceWindow.close()
        setNfAlert(prev => ({ ...prev, [sub_order.id]: true }))
      }
    } catch (e) {
      if (invoiceWindow) invoiceWindow.close()
      setNfAlert(prev => ({ ...prev, [sub_order.id]: true }))
    } finally {
      setNfLoading(prev => ({ ...prev, [sub_order.id]: false }))
    }
  }

  // MC-44: destino de entrega
  const _deliveryDestination = sub_order => {
    if (isServiceLine(sub_order)) return 'Após confirmação de pagamento, a licença será enviada para o e-mail que realizou a compra.'
    if (sub_order.school_is_b2c) {
      if (order.buyer_address_zipcode_delivery) {
        return `${order.buyer_address_street_delivery}, ${order.buyer_address_number_delivery}${
          order.buyer_secondary_address_number_delivery ? ', ' + order.buyer_secondary_address_number_delivery : ''
        } - ${order.buyer_address_neighborhood_delivery} · ${order.buyer_address_city_delivery} - ${order.buyer_address_state_delivery}, ${order.buyer_address_zipcode_delivery}`
      }
      return `${order.buyer_address_street}, ${order.buyer_address_number}${
        order.buyer_secondary_address_number ? ', ' + order.buyer_secondary_address_number : ''
      } - ${order.buyer_address_neighborhood} · ${order.buyer_address_city} - ${order.buyer_address_state}, ${order.buyer_address_zipcode}`
    }
    return 'Os livros serão entregues na escola'
  }

  const _subTotal = products => {
    let total = 0
    products.forEach(p => {
      if (!p.cancelled_at) total += parseFloat(p.amount_discounted)
    })
    return total
  }

  // Andamento agregado (adição visual do design; derivado dos mesmos status)
  const stageOf = code =>
    ['AP', 'PG', 'PAV', 'PP', 'PF', 'PT', 'PET', 'PE'].includes(code)
      ? 3
      : ['PW', 'BAP', 'NP', 'DV'].includes(code)
        ? 2
        : ['C', 'CM', 'CBS', 'CES'].includes(code)
          ? 0
          : 1
  const activeStages = allSubs.map(s => stageOf(subOrdersStatus[s.id])).filter(s => s > 0)
  const STAGES = ['Pedido realizado', 'Aguardando pagamento', 'Pagamento confirmado']
  const reached = STAGES.map((_n, i) => activeStages.filter(s => s >= i + 1).length)
  const allCancelled = activeStages.length === 0 && allSubs.length > 0
  const progressNote = allCancelled
    ? 'Pedido cancelado'
    : Math.min(...activeStages) >= 3
      ? 'Todos os subpedidos com pagamento confirmado'
      : `Aguardando o pagamento para liberar os ${activeStages.length} subpedidos`

  const showDue = order.payment_type === 'B' ? 'Vencimento: ' + order.billet_date_due : null
  const isImmediateDelivery = order.immediate_delivery == 1
  const estimatedDelivery = allSubs[0] && allSubs[0].estimated_delivery_date
  const generalStatus = OS[orderStatus] || OS['null']
  const totalDisplay = parseFloat(String(order.total || '').replace(/[^0-9,-]/g, '').replace(',', '.')) < 0 ? 'R$ 0,00' : order.total

  return (
    <V2AccountLayout>
      <Link to={back.to} id="v2-order-back" style={{ fontSize: '13px', fontWeight: '700', color: '#6B6664', textDecoration: 'none' }}>
        {back.label}
      </Link>

      <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0, fontFamily: 'Poppins,sans-serif', fontWeight: '600', fontSize: '30px', lineHeight: '1.1', letterSpacing: '-0.6px', color: '#373535' }}>
          Pedido SMD-{order.order_id}
        </h1>
        <V2StatusPill {...generalStatus} code={orderStatus} id="v2-order-status" />
        <span style={{ flex: 1 }} />
        {/* MC-23/24/25: ações administrativas — o comprador não tem as permissões */}
        {orderStatus !== 'C' && orderStatus !== 'CM' && orderStatus !== 'CBS' && orderStatus !== 'CES' && (
          <PermissionsChecker permissions={['cancel_order']}>
            <button type="button" style={dangerBtn} onClick={() => _handleCancelOrder()}>
              Cancelar pedido
            </button>
          </PermissionsChecker>
        )}
        {orderStatus === 'DV' && order.payment_type === 'B' && (
          <PermissionsChecker permissions={['cancel_order']}>
            <button type="button" style={ghostBtn} onClick={() => _handleApproveOrder()}>
              Aprovar pedido
            </button>
          </PermissionsChecker>
        )}
        <PermissionsChecker permissions={['export_order_data']}>
          <FileDownloader onClick={() => _handleExportOrder()} filename={`order-${order.order_id}.xls`}>
            <button type="button" style={ghostBtn}>Exportar dados</button>
          </FileDownloader>
        </PermissionsChecker>
      </div>

      {/* Cartão-resumo: dados / pagamento / totais (MC-26..MC-33) */}
      <section style={{ marginTop: '20px', background: '#fff', border: '1px solid #EFE7E3', borderRadius: '12px', overflow: 'hidden' }}>
        <div className="v2-acc-summary-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,minmax(0,1fr))', gap: '1px', background: '#EFE7E3' }}>
          <div style={{ background: '#fff', padding: '20px 22px' }}>
            <span style={microLabel}>Data da compra</span>
            <span style={microValue}>{order.created}</span>
            {(isImmediateDelivery || estimatedDelivery) && (
              <React.Fragment>
                <span style={{ ...microLabel, marginTop: '14px' }}>Previsão de entrega</span>
                <span style={microValue}>
                  {isImmediateDelivery
                    ? 'Em 48 horas ou de acordo com orientação da escola'
                    : new Date(estimatedDelivery + 'T00:00:00').toLocaleDateString('pt-BR')}
                </span>
              </React.Fragment>
            )}
            <span style={{ ...microLabel, marginTop: '14px' }}>Comprador(a)</span>
            <span style={microValue}>{order.buyer_name}</span>
          </div>

          <div style={{ background: '#fff', padding: '20px 22px' }}>
            <span style={microLabel}>Forma de pagamento</span>
            <div style={{ marginTop: '8px', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
              <img src={(payment[paymentType] || {}).icon} alt="" style={{ height: '26px', flex: 'none' }} />
              <div style={{ minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: '15px', fontWeight: '600', color: '#373535' }}>{(payment[paymentType] || {}).name}</span>
                {paymentType === 'R' ? (
                  payment.R.cards ? (
                    payment.R.cards.map((card, i) => (
                      <div key={i} style={{ marginTop: '4px', fontSize: '13px', color: '#6B6664' }}>
                        <strong style={{ color: '#2E5496' }}>Cartão final {card.card_number}</strong>
                        <span style={{ display: 'block' }}>
                          {parseFloat(card.total).toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })} em {card.payment_condition}x s/ juros
                        </span>
                      </div>
                    ))
                  ) : (
                    <span style={{ display: 'block', marginTop: '2px', fontSize: '13.5px', color: '#6B6664' }}>
                      {order.original_total} em {order.payment_condition}x s/ juros
                    </span>
                  )
                ) : (
                  <span style={{ display: 'block', marginTop: '2px', fontSize: '13.5px', color: '#6B6664' }}>{showDue}</span>
                )}
                {order.txid && <span style={{ display: 'block', marginTop: '2px', fontSize: '12.5px', color: '#6B6664' }}>ID: {order.txid}</span>}

                <div style={{ marginTop: '10px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {/* MC-31 */}
                  {orderStatus === 'C' && (payment[paymentType] || {}).name !== 'PIX' && (
                    <PermissionsChecker permissions={['cancel_order_billet']}>
                      <button type="button" style={dangerBtn} onClick={() => _handleCancelOrder()}>
                        {(payment[paymentType] || {}).name === 'Cartão de crédito' ? 'Solicitar estorno' : 'Cancelar boleto'}
                      </button>
                    </PermissionsChecker>
                  )}
                  {/* MC-29 */}
                  {(orderStatus === 'NP' || orderStatus === 'BAP') && order.billet_url && (
                    <button id="v2-order-billet2" type="button" style={ghostBtn} onClick={() => window.open(order.billet_url)}>
                      2ª via de boleto
                    </button>
                  )}
                  {/* MC-30 */}
                  {orderStatus === 'PW' && (
                    <button id="v2-order-pixkey" type="button" style={ghostBtn} onClick={() => setShowPixModal(true)}>
                      Ver chave PIX
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div style={{ background: '#FAF7F5', padding: '20px 22px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
              <span style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', fontSize: '13.5px', color: '#6B6664' }}>
                <span>Subtotal</span>
                <span style={{ textAlign: 'right' }}>
                  {order.prev_subtotal && <span style={{ display: 'block', textDecoration: 'line-through', color: '#8E8783' }}>{order.prev_subtotal}</span>}
                  {order.subtotal}
                </span>
              </span>
              <span style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', fontSize: '13.5px', color: '#6B6664' }}>
                <span>Desconto</span>
                <span>{order.discount}</span>
              </span>
              <span style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', fontSize: '13.5px', color: '#6B6664' }}>
                <span>Frete</span>
                <span>{order.freight}</span>
              </span>
              <span style={{ marginTop: '4px', paddingTop: '10px', borderTop: '1px solid #EFE7E3', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px' }}>
                <span style={{ fontFamily: 'Poppins,sans-serif', fontWeight: '600', fontSize: '14px', color: '#373535' }}>Total</span>
                <span id="v2-order-total" style={{ fontFamily: 'Poppins,sans-serif', fontWeight: '600', fontSize: '22px', color: '#373535' }}>{totalDisplay}</span>
              </span>
            </div>
          </div>
        </div>
        {/* pedido de negócio (09/09, item 11): a nota fiscal fica no topo, no resumo —
            um botão por subpedido (MC-43: campanha > 11, consulta SAP ao vivo) */}
        {order.campaign_id > 11 && allSubs.length > 0 && !['PW', 'BAP', 'NP', 'null', ''].includes(String(orderStatus)) && (
          <div id="v2-order-docs" style={{ padding: '14px 22px', borderTop: '1px solid #EFE7E3', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <span style={{ ...microLabel, marginRight: '4px' }}>Nota fiscal</span>
            {allSubs.map(sub => (
              <button
                key={sub.id}
                id={`v2-order-nf-${sub.id}`}
                type="button"
                style={{ ...ghostBtn, borderColor: '#D8262C', color: '#D8262C', cursor: nfLoading[sub.id] ? 'wait' : 'pointer', opacity: nfLoading[sub.id] ? 0.6 : 1 }}
                onClick={() => _handleDownloadNf(sub)}
              >
                {nfLoading[sub.id] ? 'Consultando…' : allSubs.length > 1 ? `Baixar NF · ${sub.business_line_name}` : 'Baixar NF'}
              </button>
            ))}
            {allSubs.some(sub => nfAlert[sub.id]) && (
              <span style={{ fontSize: '13px', color: '#C2262B' }}>Nota fiscal em processamento. Tente novamente mais tarde.</span>
            )}
          </div>
        )}

        {/* Andamento agregado do pedido */}
        <div style={{ borderTop: '1px solid #EFE7E3', padding: '20px 22px', background: '#fff' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
            <span style={{ fontFamily: 'Poppins,sans-serif', fontWeight: '600', fontSize: '14.5px', color: '#373535' }}>Andamento do pedido</span>
            <span style={{ fontSize: '13px', color: '#6B6664' }}>{progressNote}</span>
          </div>
          <div className="v2-acc-stages" style={{ marginTop: '16px', display: 'grid', gridTemplateColumns: 'repeat(3,minmax(0,1fr))', gap: '10px' }}>
            {STAGES.map((name, i) => {
              const all = activeStages.length > 0 && reached[i] === activeStages.length
              return (
                <div key={name} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ height: '6px', borderRadius: '100px', background: all ? '#D8262C' : reached[i] ? '#f7b4b0' : '#e9e7e4' }} />
                  <span style={{ fontFamily: 'Poppins,sans-serif', fontWeight: '600', fontSize: '13.5px', lineHeight: '1.3', color: reached[i] ? '#373535' : '#8E8783' }}>{name}</span>
                  <span style={{ fontSize: '12.5px', color: '#6B6664' }}>
                    {all ? 'todos os subpedidos' : reached[i] ? `${reached[i]} de ${activeStages.length} subpedidos` : '—'}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Blocos por aluno (MC-34) + subpedidos */}
      {Object.keys(sub_orders).map(student => {
        const subs = sub_orders[student]
        const first = subs[0]
        const collapsed = !!collapsedStudents[student]
        return (
          <React.Fragment key={first.id}>
            <div style={{ marginTop: '16px', padding: '16px 20px', background: '#fff', border: '1px solid #EFE7E3', borderRadius: '12px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '20px', flexWrap: 'wrap' }}>
              <div>
                <span style={{ display: 'block', fontFamily: 'Poppins,sans-serif', fontWeight: '600', fontSize: '15px', color: '#373535' }}>{first.student_name}</span>
                <span style={{ display: 'block', marginTop: '3px', fontSize: '13.5px', color: '#6B6664' }}>
                  {first.school_name} · voucher {first.voucher_name}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setCollapsedStudents(prev => ({ ...prev, [student]: !prev[student] }))}
                style={{ border: 'none', background: 'none', padding: 0, fontSize: '13px', fontWeight: '700', color: '#D8262C', cursor: 'pointer' }}
              >
                {collapsed ? 'Mostrar mais ⌄' : 'Mostrar menos ⌃'}
              </button>
            </div>

            {!collapsed && (
              <React.Fragment>
                {subs.map((sub_order, idx) => {
                  const st = _getSubOrderStatus(sub_order.id, sub_order.school_is_b2c)
                  const open = !!openSubs[sub_order.id]
                  const steps = buildTrackingSteps(sub_order, order)
                  const hasCancelledItem = sub_order.products.some(p => p.cancelled_at !== null)
                  const subTotal = _subTotal(sub_order.products)
                  const hasPF = sub_order.history.some(h => h.status === 'PF')
                  const hasPT = sub_order.history.some(h => h.status === 'PT')
                  // !! evita o "0" renderizado quando school_is_b2c é 0 numérico
                  const showTrackFooter = sub_order.business_line_id !== 7 && !!sub_order.school_is_b2c

                  return (
                    <section key={idx} className="v2-acc-suborder" style={{ marginTop: '12px', background: '#fff', border: '1px solid #EFE7E3', borderRadius: '12px', overflow: 'hidden' }}>
                      <div className="v2-acc-sub-head" style={{ padding: '18px 22px', display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 200px 140px', gap: '20px', alignItems: 'center' }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                            <span style={{ padding: '4px 10px', borderRadius: '6px', background: '#FDEAEA', color: '#D8262C', fontSize: '11.5px', fontWeight: '700', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                              {sub_order.business_line_name}
                            </span>
                            <span style={{ fontFamily: 'Poppins,sans-serif', fontWeight: '600', fontSize: '15px', color: '#373535' }}>{sub_order.sub_order_code}</span>
                          </div>
                          {/* Discovery: produtos visíveis sem expandir o card */}
                          {(() => {
                            const names = sub_order.products.filter(p => !p.cancelled_at).map(p => p.product_name)
                            if (!names.length) return null
                            return (
                              <span className="v2-acc-sub-products" style={{ display: 'block', marginTop: '7px', fontSize: '12.5px', lineHeight: '1.5', color: '#6B6664' }}>
                                {names.slice(0, 2).join(' · ')}
                                {names.length > 2 ? ` e mais ${names.length - 2}` : ''}
                              </span>
                            )
                          })()}
                          <span style={{ display: 'block', marginTop: '7px', fontSize: '13px', lineHeight: '1.5', color: '#6B6664' }}>
                            <strong style={{ color: '#6B6664' }}>Entrega:</strong> {_deliveryDestination(sub_order)}
                          </span>
                        </div>
                        <div>
                          <V2StatusPill {...st} />
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <span style={{ display: 'block', fontFamily: 'Poppins,sans-serif', fontWeight: '600', fontSize: '17px', color: '#373535' }}>
                            {formatMoney(subTotal, 2, 2)}
                          </span>
                          <button
                            type="button"
                            className="v2-acc-sub-toggle"
                            onClick={() => setOpenSubs(prev => ({ ...prev, [sub_order.id]: !prev[sub_order.id] }))}
                            style={{ marginTop: '6px', border: 'none', background: 'none', padding: 0, fontSize: '12.5px', fontWeight: '700', color: '#D8262C', cursor: 'pointer' }}
                          >
                            {open ? 'ocultar detalhes' : 'ver detalhes'}
                          </button>
                        </div>
                      </div>

                      {hasCancelledItem && (
                        <div style={{ margin: '0 22px 14px', border: '1px solid #f2e0b6', borderLeft: '4px solid #e0a52a', borderRadius: '12px', background: '#FFF4E2', padding: '12px 16px', fontSize: '13px', color: '#8A5A10' }}>
                          <strong>Item cancelado neste pedido!</strong> Um dos itens deste pedido foi cancelado. O valor total já foi
                          atualizado e, se aplicável, o reembolso será processado.
                        </div>
                      )}

                      {/* Tracker (MC-50..52, lógica compartilhada com o V1) */}
                      <div className="v2-acc-track" style={{ padding: '0 22px 20px', display: 'grid', gridTemplateColumns: `repeat(${steps.length},minmax(0,1fr))`, gap: '8px' }}>
                        {steps.map((step, i) => {
                          const on = !step.disabled
                          const nextOn = steps[i + 1] && !steps[i + 1].disabled
                          return (
                            <div key={step.status} style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '7px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                                <span style={{ flex: 'none', width: '11px', height: '11px', borderRadius: '50%', background: on ? step.color : '#fff', border: `2px solid ${on ? step.color : '#E3DBD7'}` }} />
                                {i < steps.length - 1 && <span style={{ flex: 1, height: '2px', background: nextOn ? steps[i + 1].color : '#e9e7e4' }} />}
                              </div>
                              <span style={{ fontSize: '12px', lineHeight: '1.3', color: on ? '#373535' : '#8E8783', paddingRight: '8px' }}>{step.label}</span>
                              <span style={{ fontSize: '11px', color: '#8E8783' }}>{on && step.changed ? moment(step.changed).format('DD/MM/YYYY') : ''}</span>
                            </div>
                          )
                        })}
                      </div>

                      {/* MC-46: nota de entrega + rastreio (B2C, linha ≠ 7) */}
                      {showTrackFooter && (hasPF || hasPT) && (
                        <div id={`v2-order-track-${sub_order.id}`} style={{ padding: '0 22px 16px', fontSize: '13px', color: '#6B6664' }}>
                          {hasPF && (
                            <p style={{ margin: 0 }}>
                              <strong>Nota de entrega: {sub_order.nf_number || '##########'}</strong>
                            </p>
                          )}
                          {hasPT && (
                            <p style={{ margin: '4px 0 0' }}>
                              <a target="_blank" rel="noreferrer" href={formatOldTrackingLink(sub_order.tracking_link) || '#'} style={{ color: '#D8262C', fontWeight: '700' }}>
                                Clique aqui para acompanhar a entrega
                              </a>
                            </p>
                          )}
                        </div>
                      )}

                      {nfAlert[sub_order.id] && (
                        <div style={{ margin: '0 22px 16px', borderRadius: '12px', background: '#FDEAEA', padding: '12px 16px', fontSize: '13px', color: '#C2262B' }}>
                          Sua nota fiscal está sendo processada, pedimos que aguarde e logo será disponibilizada.
                        </div>
                      )}

                      {open && (
                        <div style={{ padding: '0 22px 22px' }}>
                          <div style={{ border: '1px solid #EFE7E3', borderRadius: '12px', overflow: 'hidden' }}>
                            <div className="v2-acc-items-row v2-acc-items-head" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 140px 80px 110px 110px', gap: '16px', padding: '11px 16px', background: '#FAF7F5', borderBottom: '1px solid #EFE7E3' }}>
                              <span style={microLabel}>Produto</span>
                              <span style={microLabel}>Matéria</span>
                              <span style={microLabel}>Ano</span>
                              <span style={{ ...microLabel, textAlign: 'right' }}>Total</span>
                              <span style={{ ...microLabel, textAlign: 'right' }}></span>
                            </div>
                            {sub_order.products.map((p, pi) => {
                              const cancelled = !_.isEmpty(p.cancelled_at)
                              const cell = { fontSize: '13.5px', color: cancelled ? '#8E8783' : '#373535', textDecoration: cancelled ? 'line-through' : 'none' }
                              return (
                                <div key={pi} className="v2-acc-items-row" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 140px 80px 110px 110px', gap: '16px', padding: '12px 16px', borderBottom: '1px solid #F4ECE9', alignItems: 'center' }}>
                                  <span style={cell}>
                                    {p.product_name}
                                    {p.bookLicenses &&
                                      Object.entries(p.bookLicenses).map(([key, lic]) => (
                                        <span key={key} style={{ display: 'block', fontSize: '12px', color: '#6B6664' }}>
                                          <b>{lic.component || 'Chave da licença'}: </b>
                                          {lic.license_code}
                                        </span>
                                      ))}
                                  </span>
                                  <span style={{ ...cell, color: cancelled ? '#8E8783' : '#6B6664', fontSize: '13px' }}>{p.theme}</span>
                                  <span style={{ ...cell, color: cancelled ? '#8E8783' : '#6B6664', fontSize: '13px' }}>{sub_order.serie_name}</span>
                                  <span style={{ ...cell, fontWeight: '700', textAlign: 'right' }}>{formatMoney(p.amount_discounted, 2, 2)}</span>
                                  <span style={{ textAlign: 'right' }}>
                                    {cancelled ? (
                                      <V2StatusPill type="warning" description="Cancelado" />
                                    ) : (
                                      <React.Fragment>
                                        <PermissionsChecker permissions={['cancel_order_item']}>
                                          <button type="button" style={{ ...dangerBtn, height: '30px', padding: '0 10px', fontSize: '11.5px' }} onClick={() => _handleCancelOrderItem(p)}>
                                            Cancelar item
                                          </button>
                                        </PermissionsChecker>
                                        <PermissionsChecker permissions={['cancel_order_item']} negate={true}>
                                          <V2StatusPill type="ok" description="Incluído" />
                                        </PermissionsChecker>
                                      </React.Fragment>
                                    )}
                                  </span>
                                </div>
                              )
                            })}
                          </div>

                          <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'flex-end', gap: '12px', alignItems: 'baseline' }}>
                            <span style={{ fontSize: '13px', color: '#6B6664' }}>Total</span>
                            <span style={{ fontFamily: 'Poppins,sans-serif', fontWeight: '600', fontSize: '18px', color: '#373535' }}>{formatMoney(subTotal, 2, 2)}</span>
                          </div>

                          {/* MC-49: ativação de licenças digitais */}
                          {variables.licenseActivationLink && sub_order.products.some(p => p.bookLicenses && p.bookLicenses.length > 0) && (
                            <div style={{ marginTop: '12px', border: '1px solid #EFE7E3', borderRadius: '12px', padding: '14px 16px', fontSize: '13.5px', color: '#6B6664' }}>
                              <p style={{ margin: 0 }}>
                                Ativação das licenças na plataforma <b>{sub_order.digital_solution || ''}</b>
                              </p>
                              <p style={{ margin: '6px 0 0' }}>
                                <a target="_blank" rel="noreferrer" href={variables.licenseActivationLink} style={{ color: '#D8262C', fontWeight: '700' }}>
                                  Clique aqui para acessar o tutorial de ativação.
                                </a>
                              </p>
                            </div>
                          )}

                          <div style={{ marginTop: '14px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            {/* MC-42 */}
                            {(st.code === 'AP' || st.code === 'PG' || st.code === 'PAV') && (
                              <button id={`v2-sub-receipt-${sub_order.id}`} type="button" style={ghostBtn} onClick={() => _printReceipt(sub_order)}>
                                Comprovante
                              </button>
                            )}
                            {/* MC-43: "Baixar NF" subiu para o cartão-resumo (#v2-order-docs) */}
                            {/* MC-41 */}
                            {st.code !== 'C' && st.code !== 'CBS' && st.code !== 'CES' && (
                              <PermissionsChecker permissions={['cancel_order']}>
                                <button type="button" style={dangerBtn} onClick={() => _handleCancelOrder(sub_order.id)}>
                                  Cancelar pedido
                                </button>
                              </PermissionsChecker>
                            )}
                          </div>
                        </div>
                      )}
                    </section>
                  )
                })}

                {/* MC-35 */}
                <div style={{ marginTop: '14px', border: '1px solid #f2e0b6', borderLeft: '4px solid #e0a52a', borderRadius: '12px', background: '#FFF4E2', padding: '14px 18px' }}>
                  <p style={{ margin: 0, fontSize: '13.5px', lineHeight: '1.55', color: '#8A5A10' }}>
                    Devido ao grande volume de pedidos, alguns subpedidos podem acabar sendo entregues em momentos diferentes. Os
                    status estarão sendo atualizados diariamente nos detalhes do pedido.
                  </p>
                </div>
              </React.Fragment>
            )}
          </React.Fragment>
        )
      })}

      {showPixModal && (
        <PixPaymentModal
          order={{
            order_id: order.order_id,
            pix_copy_paste: order.pix_copy_paste,
            pix_qrcode: order.pix_qrcode,
            pix_expiration: order.pix_expiration,
            txid: order.txid,
          }}
          total={order.total}
          onClose={() => setShowPixModal(false)}
        />
      )}
    </V2AccountLayout>
  )
}
